import {
  TECHNIQUE_LEVELS,
  type ResolvedTechnique,
  type TechniqueLevel,
  type TechniqueMechanicsDefinition,
  type TechniqueTargeting,
  type TechniqueBehaviorDefinition,
} from "./TechniqueDefinition.js";

const VALID_LEVELS = new Set<number>(TECHNIQUE_LEVELS);

export function resolveTechniqueLevel<T extends TechniqueMechanicsDefinition>(
  definition: T,
  level: number,
): ResolvedTechnique<T> {
  if (!VALID_LEVELS.has(level)) {
    throw new RangeError(
      `Technique ${definition.id} level must be an integer between 1 and 10; received ${level}.`,
    );
  }

  const techniqueLevel = level as TechniqueLevel;
  const { levels, ...metadata } = definition;
  const parameters = levels[techniqueLevel];
  if (!parameters) {
    throw new Error(
      `Technique ${definition.id} is missing its level ${techniqueLevel} definition.`,
    );
  }

  return {
    ...metadata,
    ...parameters,
    ...(parameters.control ? { control: { ...parameters.control } } : {}),
    level: techniqueLevel,
  } as ResolvedTechnique<T>;
}

export function validateTechniqueDefinition(
  definition: TechniqueMechanicsDefinition,
): void {
  if (!definition.id.trim()) throw new Error("Technique id cannot be empty.");
  if (!definition.name.trim())
    throw new Error(`Technique ${definition.id} name cannot be empty.`);

  const behaviorKind = definition.behavior?.kind;
  const expectedTargetingByBehavior: Partial<
    Record<TechniqueBehaviorDefinition["kind"], readonly TechniqueTargeting[]>
  > = {
    damage: [
      "frontal_cone",
      "projectile",
      "circle",
      "point_area",
      "single_hostile",
    ],
    healing: ["ally_injured"],
    restorative_barrier: ["ally_injured_or_threatened"],
    control: ["single_hostile"],
    trap: ["single_hostile"],
    field: ["point_area"],
    guard: ["self"],
    counter: ["reactive_counter"],
  } as const;
  if (!behaviorKind)
    throw new Error(`Technique ${definition.id} is missing its behavior.`);
  const expectedTargetings = expectedTargetingByBehavior[behaviorKind];
  if (
    expectedTargetings &&
    !expectedTargetings.some((targeting) => targeting === definition.targeting)
  ) {
    throw new Error(
      `Technique ${definition.id} behavior ${behaviorKind} is incompatible with targeting ${definition.targeting}.`,
    );
  }
  if (definition.deployment && behaviorKind !== "trap") {
    throw new Error(
      `Technique ${definition.id} deployment settings require trap behavior.`,
    );
  }
  if (definition.field && behaviorKind !== "field") {
    throw new Error(
      `Technique ${definition.id} field settings require field behavior.`,
    );
  }
  if (definition.reactive?.kind === "guard" && behaviorKind !== "guard") {
    throw new Error(
      `Technique ${definition.id} guard settings require guard behavior.`,
    );
  }
  if (definition.reactive?.kind === "counter" && behaviorKind !== "counter") {
    throw new Error(
      `Technique ${definition.id} counter settings require counter behavior.`,
    );
  }

  for (const level of TECHNIQUE_LEVELS) {
    const values = definition.levels[level];
    if (!values) {
      throw new Error(`Technique ${definition.id} is missing level ${level}.`);
    }

    for (const [field, value] of Object.entries(values)) {
      if (typeof value === "number" && !Number.isFinite(value)) {
        throw new Error(
          `Technique ${definition.id} level ${level} has non-finite ${field}.`,
        );
      }
    }

    if (
      values.chakraCost < 0 ||
      values.cooldownMs < 0 ||
      (values.impactDelayMs !== undefined && values.impactDelayMs < 0) ||
      values.range <= 0 ||
      !Number.isInteger(values.maxTargets) ||
      values.maxTargets < 1 ||
      values.power < 0 ||
      values.attackCoefficient < 0
    ) {
      throw new Error(
        `Technique ${definition.id} level ${level} has invalid base mechanics.`,
      );
    }

    if (
      definition.targeting === "frontal_cone" &&
      (values.coneDegrees === undefined ||
        values.coneDegrees <= 0 ||
        values.coneDegrees > 360)
    ) {
      throw new Error(
        `Technique ${definition.id} level ${level} requires a cone angle in (0, 360].`,
      );
    }

    if (
      (definition.targeting === "circle" ||
        definition.targeting === "point_area") &&
      (values.radius === undefined ||
        values.radius <= 0 ||
        (values.minimumTargets !== undefined &&
          (!Number.isInteger(values.minimumTargets) ||
            values.minimumTargets < 1 ||
            values.minimumTargets > values.maxTargets)))
    ) {
      throw new Error(
        `Technique ${definition.id} level ${level} has invalid circular targeting values.`,
      );
    }

    if (
      definition.deployment?.kind === "proximity_trap" &&
      (definition.targeting !== "single_hostile" ||
        values.triggerRadius === undefined ||
        values.triggerRadius <= 0 ||
        values.explosionRadius === undefined ||
        values.explosionRadius <= 0 ||
        values.triggerRadius > values.explosionRadius)
    ) {
      throw new Error(
        `Technique ${definition.id} level ${level} has invalid proximity trap values.`,
      );
    }

    if (
      definition.targeting === "ally_injured" &&
      (values.injuredHpThreshold === undefined ||
        values.injuredHpThreshold <= 0 ||
        values.injuredHpThreshold > 1)
    ) {
      throw new Error(
        `Technique ${definition.id} level ${level} requires an injured HP threshold in (0, 1].`,
      );
    }

    const hasHealingValues =
      values.healBase !== undefined || values.healCoefficient !== undefined;
    const hasShieldValues =
      values.shieldBase !== undefined ||
      values.shieldCoefficient !== undefined ||
      values.shieldDurationMs !== undefined;
    if (definition.targeting === "ally_injured_or_threatened") {
      if (
        !definition.shieldKey?.trim() ||
        values.healBase === undefined ||
        values.healCoefficient === undefined ||
        values.shieldBase === undefined ||
        values.shieldCoefficient === undefined ||
        values.shieldDurationMs === undefined ||
        values.healBase < 0 ||
        values.healCoefficient < 0 ||
        values.shieldBase < 0 ||
        values.shieldCoefficient < 0 ||
        values.shieldDurationMs <= 0
      ) {
        throw new Error(
          `Technique ${definition.id} level ${level} requires valid healing, shield, duration, and shield identity values.`,
        );
      }
    } else if (hasHealingValues || hasShieldValues || definition.shieldKey) {
      if (
        values.healBase === undefined ||
        values.healCoefficient === undefined ||
        values.shieldBase === undefined ||
        values.shieldCoefficient === undefined ||
        values.shieldDurationMs === undefined ||
        !definition.shieldKey?.trim() ||
        values.healBase < 0 ||
        values.healCoefficient < 0 ||
        values.shieldBase < 0 ||
        values.shieldCoefficient < 0 ||
        values.shieldDurationMs <= 0
      ) {
        throw new Error(
          `Technique ${definition.id} level ${level} has incomplete or invalid healing/shield values.`,
        );
      }
    }

    if (definition.targeting === "single_hostile" && values.control) {
      const control = values.control;
      if (
        !Number.isFinite(control.fearMs) ||
        !Number.isFinite(control.potency) ||
        !Number.isFinite(control.damageReduction) ||
        !Number.isFinite(control.debuffMs) ||
        control.fearMs < 0 ||
        control.potency < 0 ||
        control.damageReduction < 0 ||
        control.damageReduction > 1 ||
        control.debuffMs < 0
      ) {
        throw new Error(
          `Technique ${definition.id} level ${level} has invalid control values.`,
        );
      }
    }

    if (definition.reactive?.kind === "guard") {
      if (
        definition.targeting !== "self" ||
        values.damageReduction === undefined ||
        values.damageReduction <= 0 ||
        values.damageReduction >= 1 ||
        values.defenseWindowMs === undefined ||
        values.defenseWindowMs <= 0 ||
        values.counterDamageBonus !== undefined
      ) {
        throw new Error(
          "Technique " +
            definition.id +
            " level " +
            level +
            " has invalid defensive reactive values.",
        );
      }
    } else if (definition.reactive?.kind === "counter") {
      if (
        definition.targeting !== "reactive_counter" ||
        definition.reactive.readyWindowMs !== 4_000 ||
        definition.reactive.expiresOnNextAction !== true ||
        values.counterDamageBonus === undefined ||
        values.counterDamageBonus <= 0 ||
        values.counterDamageBonus > 1 ||
        values.damageReduction !== undefined ||
        values.defenseWindowMs !== undefined
      ) {
        throw new Error(
          "Technique " +
            definition.id +
            " level " +
            level +
            " has invalid counter reactive values.",
        );
      }
    } else if (
      definition.targeting === "self" ||
      definition.targeting === "reactive_counter" ||
      values.damageReduction !== undefined ||
      values.defenseWindowMs !== undefined ||
      values.counterDamageBonus !== undefined
    ) {
      throw new Error(
        "Technique " +
          definition.id +
          " level " +
          level +
          " is missing its reactive behavior definition.",
      );
    }
  }

  const deployment = definition.deployment;
  if (
    deployment?.kind === "proximity_trap" &&
    (!Number.isFinite(deployment.armingTimeMs) ||
      deployment.armingTimeMs < 0 ||
      !Number.isFinite(deployment.lifetimeMs) ||
      deployment.lifetimeMs <= deployment.armingTimeMs ||
      deployment.activeLimitPerCaster !== 1 ||
      deployment.recast !== "replace" ||
      deployment.placement !== "target_position")
  ) {
    throw new Error(
      `Technique ${definition.id} has invalid proximity trap deployment settings.`,
    );
  }

  const field = definition.field;
  if (
    behaviorKind === "trap" &&
    definition.deployment?.kind !== "proximity_trap"
  ) {
    throw new Error(
      `Technique ${definition.id} trap behavior requires deployment settings.`,
    );
  }
  if (behaviorKind === "field" && !field) {
    throw new Error(
      `Technique ${definition.id} field behavior requires field settings.`,
    );
  }
  if (behaviorKind === "guard" && definition.reactive?.kind !== "guard") {
    throw new Error(
      `Technique ${definition.id} guard behavior requires guard settings.`,
    );
  }
  if (behaviorKind === "counter" && definition.reactive?.kind !== "counter") {
    throw new Error(
      `Technique ${definition.id} counter behavior requires counter settings.`,
    );
  }
  if (
    behaviorKind === "control" &&
    TECHNIQUE_LEVELS.some((level) => !definition.levels[level]?.control)
  ) {
    throw new Error(
      `Technique ${definition.id} control behavior requires control values at every level.`,
    );
  }
  if (behaviorKind === "restorative_barrier" && !definition.shieldKey?.trim()) {
    throw new Error(
      `Technique ${definition.id} restorative barrier behavior requires a shield identity.`,
    );
  }
  if (field) {
    if (
      definition.targeting !== "point_area" ||
      field.kind !== "persistent_field" ||
      !Number.isFinite(field.durationMs) ||
      field.durationMs <= 0 ||
      !Number.isFinite(field.pulseIntervalMs) ||
      field.pulseIntervalMs <= 0 ||
      !Number.isInteger(field.pulseCount) ||
      field.pulseCount < 1 ||
      field.pulseCount * field.pulseIntervalMs !== field.durationMs ||
      field.activeLimitPerCaster !== 1 ||
      field.recast !== "replace" ||
      field.placement !== "target_position"
    ) {
      throw new Error(
        `Technique ${definition.id} has invalid persistent field settings.`,
      );
    }
    for (const level of TECHNIQUE_LEVELS) {
      const values = definition.levels[level]!;
      if (
        values.fieldSlow === undefined ||
        !Number.isFinite(values.fieldSlow) ||
        values.fieldSlow < 0 ||
        values.fieldSlow >= 1
      ) {
        throw new Error(
          `Technique ${definition.id} level ${level} requires field slow magnitude in [0, 1).`,
        );
      }
    }
  } else if (
    TECHNIQUE_LEVELS.some(
      (level) => definition.levels[level]?.fieldSlow !== undefined,
    )
  ) {
    throw new Error(
      `Technique ${definition.id} defines field slow values without a field.`,
    );
  }
}
